/**
 * @fileoverview Resolves template field defaults into response records for bulk bypass.
 * @module inspection/bulkBypassDefaults
 *
 * Responsibilities:
 *   - Walk every standard form in a template and build a QuestionResponse-shaped
 *     record for each processable field using its configured default value.
 *   - Skip display-only fields (instructions, section) — they produce no response.
 *   - Skip bulk-skipped fields (camera, photo, signature, location, repeatableList,
 *     inspectors, dataBound) — these are not populated during bulk.
 *   - Skip module forms (contacts, violations) entirely.
 *   - Resolve extras-based defaults for toggle (defaultState), date (defaultToToday),
 *     and dateTime (defaultToNow).
 *   - When autoComplete is true, override pass/fail fields to their passing value
 *     so bulk-completed cases always show 100% compliance with no violations.
 *
 * Dependencies: none (pure functions, zero runtime dependencies).
 *
 * Architecture:
 *   - Shared between mobile-pwa and tenant-web via @firesquirrel/core-domain.
 *   - Database-agnostic: operates on the same BulkAnalysisForm shape used by
 *     the bulk template analysis utility.
 *   - The output shape matches QuestionResponse so it can be consumed directly
 *     by the bulk apply flow without transformation.
 *
 * Notes:
 *   - Toggle defaults: 'on' → true, 'off' → false. Missing/unset → undefined.
 *   - Date defaults: defaultToToday → current date ISO string (date portion only).
 *   - DateTime defaults: defaultToNow → current date+time ISO string.
 *   - Fields without a configured default produce a response with value undefined.
 *   - Pass/fail resolution (autoComplete): toggle → !failsOn, dropdown/radioGroup
 *     → first option NOT in failValues, checkboxGroup → all non-fail options.
 */

import type { FieldType } from './types';
import type { BulkAnalysisForm } from './bulkTemplateAnalysis';

// ---------------------------------------------------------------------------
// Field type sets (mirrored from bulkTemplateAnalysis for independence)
// ---------------------------------------------------------------------------

const PROCESSABLE: ReadonlySet<FieldType> = new Set([
  'toggle', 'text', 'textarea', 'number', 'measurement',
  'dropdown', 'radioGroup', 'checkboxGroup',
  'date', 'dateTime',
]);

const DISPLAY_ONLY: ReadonlySet<FieldType> = new Set([
  'instructions', 'section',
]);

const SKIPPED: ReadonlySet<FieldType> = new Set([
  'camera', 'photo', 'signature', 'location',
  'repeatableList', 'inspectors', 'dataBound',
]);

// ---------------------------------------------------------------------------
// Output type
// ---------------------------------------------------------------------------

/**
 * @contract BulkBypassResponse
 * Purpose: A single field response produced by the bypass defaults resolver.
 * Fields:  Mirrors the shape of QuestionResponse used by mobile-pwa and tenant-web.
 * Rules:   value may be undefined for fields without a configured default.
 * Sync:    Ephemeral — fed into the bulk apply flow, not persisted directly.
 */
export interface BulkBypassResponse {
  value: unknown;
  questionText: string;
  fieldType: string;
  formId: string;
  fieldId: string;
  isNA: boolean;
}

// ---------------------------------------------------------------------------
// Pass/fail field types and passing-value resolution
// ---------------------------------------------------------------------------

const FAIL_CAPABLE: ReadonlySet<FieldType> = new Set([
  'toggle', 'dropdown', 'radioGroup', 'checkboxGroup',
]);

/**
 * Resolve the PASSING value for a pass/fail-capable field.
 *
 * Toggle:         the opposite of failsOn (default: OFF fails → ON passes).
 * Dropdown/Radio: first option value NOT in failValues.
 * CheckboxGroup:  all option values NOT in failValues (empty if all fail).
 *
 * Returns undefined when the field is not pass/fail capable, not configured
 * as pass/fail, or when no passing option can be determined.
 */
function resolvePassingValue(
  fieldType: FieldType,
  extras: Record<string, unknown>,
): unknown {
  if (!extras.hasPassFail) return undefined;

  switch (fieldType) {
    case 'toggle': {
      const failsOn = typeof extras.failsOn === 'boolean' ? extras.failsOn : false;
      return !failsOn;
    }
    case 'dropdown':
    case 'radioGroup': {
      const failValues = Array.isArray(extras.failValues) ? extras.failValues as string[] : [];
      const options = Array.isArray(extras.options)
        ? (extras.options as Array<{ value?: string; label?: string }>)
        : [];
      const passingOption = options.find((opt) => {
        const val = opt.value ?? opt.label ?? '';
        return val.length > 0 && !failValues.includes(val);
      });
      return passingOption ? (passingOption.value ?? passingOption.label ?? '') : undefined;
    }
    case 'checkboxGroup': {
      const failValues = Array.isArray(extras.failValues) ? extras.failValues as string[] : [];
      const options = Array.isArray(extras.options)
        ? (extras.options as Array<{ value?: string; label?: string }>)
        : [];
      const passingValues = options
        .map((opt) => opt.value ?? opt.label ?? '')
        .filter((v) => v.length > 0 && !failValues.includes(v));
      return passingValues.length > 0 ? passingValues : [];
    }
    default:
      return undefined;
  }
}

// ---------------------------------------------------------------------------
// Default resolution
// ---------------------------------------------------------------------------

/**
 * Resolve the default value for a single field based on its type and extras.
 * Returns undefined when no default is configured.
 */
function resolveFieldDefault(
  field: { type: FieldType; defaultValue?: unknown; extras?: Record<string, unknown> },
): unknown {
  if (field.defaultValue != null) return field.defaultValue;

  const extras = (field.extras ?? {}) as Record<string, unknown>;
  switch (field.type) {
    case 'toggle': {
      const state = extras.defaultState;
      if (state === 'on') return true;
      if (state === 'off') return false;
      return undefined;
    }
    case 'date':
      if (extras.defaultToToday === true) {
        return new Date().toISOString().slice(0, 10);
      }
      return undefined;
    case 'dateTime':
      if (extras.defaultToNow === true) {
        return new Date().toISOString();
      }
      return undefined;
    default:
      return undefined;
  }
}

// ---------------------------------------------------------------------------
// Core resolver
// ---------------------------------------------------------------------------

/**
 * @contract ResolveBypassOptions
 * Purpose: Options for the bypass defaults resolver.
 * Fields:  autoComplete — when true, pass/fail fields are forced to their passing value.
 * Rules:   autoComplete overrides any configured default for pass/fail fields.
 * Sync:    Ephemeral — not persisted.
 */
export interface ResolveBypassOptions {
  autoComplete?: boolean;
}

/**
 * @process resolveBypassDefaults
 *
 * Steps:
 * 1. Iterate over forms — skip header and module forms.
 * 2. For each standard form, iterate fields.
 * 3. Skip display-only and bulk-skipped field types.
 * 4. For each processable field:
 *    a. If autoComplete AND field is pass/fail capable AND hasPassFail is set,
 *       resolve the PASSING value instead of the normal default.
 *    b. Otherwise, resolve the configured default value.
 * 5. Key the response by fieldId.
 *
 * Inputs:  Array of BulkAnalysisForm, optional ResolveBypassOptions.
 * Outputs: Record keyed by fieldId → BulkBypassResponse.
 * Failure Handling: Missing fields or forms are silently skipped.
 * Idempotency: NOT REQUIRED — date/dateTime defaults use current time.
 */
export function resolveBypassDefaults(
  forms: ReadonlyArray<BulkAnalysisForm>,
  options?: ResolveBypassOptions,
): Record<string, BulkBypassResponse> {
  const responses: Record<string, BulkBypassResponse> = {};
  const autoComplete = options?.autoComplete ?? false;

  for (const form of forms) {
    // Skip header forms (display-only rich text) and module forms (contacts, violations).
    if (form.formKind === 'header') continue;
    if (form.formKind === 'module') continue;

    for (const field of form.fields) {
      const fieldType = field.type as FieldType;
      const fieldId = field.id;
      if (!fieldId) continue;

      // Skip display-only — no response needed.
      if (DISPLAY_ONLY.has(fieldType)) continue;

      // Skip bulk-skipped — these are not populated during bulk.
      if (SKIPPED.has(fieldType)) continue;

      // Only process known processable types.
      if (!PROCESSABLE.has(fieldType)) continue;

      const extras = (field.extras ?? {}) as Record<string, unknown>;

      // When auto-completing, pass/fail fields are forced to their passing
      // value so completed cases always show 100% compliance.
      let value: unknown;
      if (autoComplete && FAIL_CAPABLE.has(fieldType) && extras.hasPassFail) {
        value = resolvePassingValue(fieldType, extras);
      } else {
        value = resolveFieldDefault({
          type: fieldType,
          defaultValue: field.defaultValue,
          extras,
        });
      }

      responses[fieldId] = {
        value,
        questionText: field.label ?? '',
        fieldType,
        formId: form._id,
        fieldId,
        isNA: false,
      };
    }
  }

  return responses;
}
